// /src/modules/profileApi.ts
import { getMeSafe } from "../core/me";
import { getApiBaseV1 } from "../core/http";
import { getBffTokenForCurrentIdentity } from "./bffTokenCache";

export type Theme = "day" | "night";

export type ProfileDto = {
  email: string;
  displayName: string;
  firstName?: string | null;
  lastName?: string | null;
  location?: string | null;
  bio?: string | null;
  company?: string | null;
  role?: string | null;

  theme: Theme; // persisted, but UI may choose not to apply yet

  joinedAtUtc: string;
  updatedAtUtc: string;
};

export type PatchProfileDto = Partial<{
  displayName: string | null;
  firstName: string | null;
  lastName: string | null;
  location: string | null;
  bio: string | null;
  company: string | null;
  role: string | null;

  theme: Theme | null; // allow null if you want "clear" semantics; otherwise keep Theme only
}>;

async function getAccessTokenOrThrow(): Promise<string> {
  const me = await getMeSafe();
  return await getBffTokenForCurrentIdentity(me);
}

async function throwWithBody(prefix: string, res: Response): Promise<never> {
  const txt = await res.text().catch(() => "");
  throw new Error(`${prefix}_${res.status}${txt ? ` — ${txt}` : ""}`);
}

export async function getProfile(): Promise<ProfileDto> {
  const tok = await getAccessTokenOrThrow();
  const url = `${getApiBaseV1()}/profile`;

  const res = await fetch(url, {
    method: "GET",
    headers: { Authorization: `Bearer ${tok}` },
  });

  if (!res.ok) return throwWithBody("PROFILE_GET", res);
  return (await res.json()) as ProfileDto;
}

export async function patchProfile(body: PatchProfileDto): Promise<ProfileDto> {
  const tok = await getAccessTokenOrThrow();
  const url = `${getApiBaseV1()}/profile`;

  // If you prefer to keep fetch here, that's fine; postJson is optional.
  // Using fetch so we can attach Authorization header and get consistent errors.

  const res = await fetch(url, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${tok}`,
    },
    body: JSON.stringify(body),
  });

  if (res.status === 403) {
    const txt = await res.text().catch(() => "");
    throw new Error(`PROFILE_FORBIDDEN${txt ? ` — ${txt}` : ""}`);
  }

  if (!res.ok) return throwWithBody("PROFILE_PATCH", res);

  return (await res.json()) as ProfileDto;
}
